import React, { useEffect, useMemo, useRef, useState } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import { inputStyle } from '../components/FormField';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/priceListApi';
import { parseCard, whatsappNumber } from '../utils/cardParser';
import { readCardText } from '../utils/cardOcr';
import { buildSheetPdfFile, buildSheetImageFile, imageFileToPngBlob } from '../utils/sheetPdf';
import { sizeKind, roleOf, isAnchor, applyAnchor } from '../utils/priceRules';
import { formatDate } from '../utils/dateFormat';
import DateInput from '../components/DateInput';
import CustomizePriceList from '../components/CustomizePriceList';

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const EMPTY_GROUP = () => ({ series_name: '', designs: [], price1: '', price2: '' });
const EMPTY_SECTION = () => ({ heading: '', packing: '', box_weight: '', footer_label: '', groups: [EMPTY_GROUP()] });

const todayIso = () => new Date().toISOString().slice(0, 10);

const NEW_DOC = () => ({
  title: '',
  customer_name: '',
  customer_mobile: '',
  customer_address: '',
  customer_city: '',
  customer_state: '',
  list_date: todayIso(),
  valid_until: '',
  col1_label: 'PREM',
  col2_label: 'STD',
  terms: '',
  special_remark: 'SPECIAL REMARK:',
  payment_terms: '',
  sections: [EMPTY_SECTION()],
});

const DEFAULT_TERMS = [
  '1. Above price are Ex-Works : Morbi (Gujarat) prices per Square Feet.',
  '2.GST and Any other Taxes whereever applicable shall be charged extra.',
].join('\n');

const grp = (series_name, price1, price2, designs) => ({ series_name, price1, price2, designs });

/** The price list from the sample the user supplied - a starting point to edit. */
const SAMPLE_DOC = () => ({
  ...NEW_DOC(),
  title: 'PRICE LIST',
  terms: DEFAULT_TERMS,
  payment_terms: 'ADVANCE PAYMENT',
  sections: [
    {
      heading: '600x600 mm DOUBLE CHARGED (2x2)',
      packing: '4 pc/Box',
      box_weight: '23.5-24 kg Approx',
      footer_label: '600x600 mm DOUBLE CHARGED',
      groups: [
        grp('PRO Series', 32, 30, ['PRO WHITE', 'PRO GOLD', 'PRO SMOKE', 'PRO CHERRY', 'PRO FOREST', 'PRO PISTA', 'PRO PINE']),
        grp('MELODY Light Series', 33, 31, ['MELODY WHITE', 'MELODY YELLOW', 'MELODY AQUA', 'MELODY PINK', 'MELODY ASH', 'MELODY SILK', 'MELODY ELITE', 'MELODY MAGENTA', 'MELODY PEARL', 'MELODY SLATE', 'MELODY ROSE RED', 'MELODY EARTH']),
        grp('MELODY Dark & PLAIN', 36, 34, ['MELODY BLACK', 'MELODY BROWN', 'PLAIN SUPER WHITE', 'SNP WHITE']),
        grp('AMAZONE & DELTA Light', 36, 34, ['DELTA WHITE', 'DELTA CREMA', 'DELTA SLATE', 'AMAZONE WHITE', 'AMAZONE CREMA', 'AMAZONE SLATE']),
      ],
    },
    {
      heading: '1200x600 mm DOUBLE CHARGED (4x2)',
      packing: '2 pc/Box',
      box_weight: '25 kg Approx',
      footer_label: '1200x600 mm DOUBLE CHARGED',
      groups: [
        grp('MELODY Light', 36.5, 33.5, ['MELODY WHITE', 'MELODY YELLOW', 'MELODY ASH', 'MELODY PINK', 'MELODY AQUA', 'MELODY EARTH']),
        grp('MELODY Dark , DELTA Light & MARBLE Light & DESSERT Light', 39.5, 36.5, ['MELODY BLACK', 'GALAXY BLACK', 'DELTA WHITE', 'DELTA CREMA', 'DELTA SLATE', 'DELTA AQUA', 'DELTA PINK', 'DELTA PACIFIC', 'DELTA GOLD', 'ICE WHITE', 'WILLIAM GREY', 'LINEAR WHITE', 'LINEAR CREMA', 'MERCURY SLATE', 'TRAVERTINE GOLD', 'LAMINA BLACK', 'DESSERT WHITE', 'DESSERT GOLD', 'DESSERT GREY', 'PLAIN SUPER WHITE', 'SNP WHITE']),
        grp('DELTA Dark & MARBLE Dark & DESSERT Dark', 42.5, 39.5, ['DELTA BLACK', 'ARIZONA TIGER', 'CHOCO TRAVERTINE', 'ARMANI CARBON', 'DESSERT BLACK PERAL', 'DESSERT BLUE PEARL']),
        grp('ALASKA Light', 41.5, 38.5, ['ALPHA WHITE', 'MIAMI CREMA', 'LASA WHITE', 'JAZZ WHITE GREY', 'BRECCIA GOLD', 'MINT MARBLE', 'SPIDER CREMA', 'ARISTONE WHITE', 'ATHENA BEIGE', 'CALACATTA WHITE', 'CASTLE VERDE']),
      ],
    },
  ],
});

function plShow(doc, field) {
  const secs = (doc && doc.sections) || [];
  let total = 0;
  let any = false;
  secs.forEach((s) => (s.groups || []).forEach((g) => {
    total += 1;
    const v = g[field];
    if (v !== null && v !== undefined && String(v).trim() !== '') any = true;
  }));
  return any || total === 0;
}

function fmtPrice(v) {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return String(Math.round(n * 100) / 100);
}

function fmtDate(d) {
  return formatDate(d);
}

function termLines(terms) {
  return String(terms || '').split('\n').map((l) => l.trim()).filter(Boolean);
}

/** "Party name, City, State" for the heading line of the sheet. */
function partyLine(doc) {
  return [doc.customer_name, doc.customer_city, doc.customer_state].filter(Boolean).join(', ');
}

function footerLabel(section) {
  return section.footer_label || String(section.heading || '').replace(/\s*\([^)]*\)\s*$/, '');
}

function errorMessage(err) {
  const e = err && err.response && err.response.data && err.response.data.error;
  if (e) return typeof e === 'string' ? e : e.message || 'Request failed';
  return (err && err.message) || 'Something went wrong';
}

/* Brand / logo lines the user has rejected as "party name" - skipped in later scans (kept in this browser). */
const IGNORE_KEY = 'pl_card_ignore';
function loadIgnore() {
  try {
    const v = JSON.parse(window.localStorage.getItem(IGNORE_KEY) || '[]');
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
  } catch (e) {
    return [];
  }
}
function saveIgnore(list) {
  try {
    window.localStorage.setItem(IGNORE_KEY, JSON.stringify(list));
  } catch (e) { /* private mode - ignore */ }
}

/** Plain-text version for WhatsApp / message sharing. */
export function buildShareText(doc) {
  const out = [];
  out.push(`*${doc.title || 'PRICE LIST'}*`);
  const meta = [];
  const party = partyLine(doc);
  if (party) meta.push(`To: ${party}`);
  if (doc.list_date) meta.push(`Date: ${fmtDate(doc.list_date)}`);
  if (doc.valid_until) meta.push(`Valid until: ${fmtDate(doc.valid_until)}`);
  if (meta.length) out.push(meta.join(' | '));
  doc.sections.forEach((s) => {
    out.push('');
    out.push(`*${s.heading}*`);
    s.groups.forEach((g) => {
      const prices = [];
      if (fmtPrice(g.price1) !== '') prices.push(`${doc.col1_label} ${fmtPrice(g.price1)}`);
      if (fmtPrice(g.price2) !== '') prices.push(`${doc.col2_label} ${fmtPrice(g.price2)}`);
      out.push(`*${g.series_name}*${prices.length ? ` - ${prices.join(' | ')}` : ''}`);
      if (g.designs.length) out.push(g.designs.join(', '));
    });
    const pack = [];
    if (s.packing) pack.push(`Packing: ${s.packing}`);
    if (s.box_weight) pack.push(`Box weight: ${s.box_weight}`);
    if (pack.length) out.push(pack.join(' | '));
  });
  const terms = termLines(doc.terms);
  if (terms.length) {
    out.push('');
    out.push('*Terms & Conditions*');
    terms.forEach((t) => out.push(t));
  }
  if (doc.payment_terms) {
    out.push('');
    out.push(`*Payment Terms:* ${doc.payment_terms}`);
  }
  return out.join('\n');
}

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // fall through to the legacy path
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const SHEET_CSS = `
.pl-sheet{background:#fff;color:#1b2430;max-width:1000px;margin:0 auto;padding:14px 16px 16px;border-top:6px solid #12355B;font-family:Inter,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;-webkit-font-smoothing:antialiased}
.pl-sheet table{width:100%;border-collapse:collapse;table-layout:fixed}
.pl-sheet td,.pl-sheet th{border:1px solid #cfd8e3;padding:8px 10px;font-size:14.5px;line-height:1.45;vertical-align:middle;word-wrap:break-word}
.pl-sheet .pl-top{border:none;text-align:center;font-weight:700;font-size:26px;letter-spacing:.8px;text-transform:uppercase;padding:6px 8px 2px;color:#12355B}
.pl-sheet .pl-meta{border:none;text-align:center;font-size:15px;font-weight:600;padding:2px 8px 14px;color:#1b2430}
.pl-sheet .pl-title td{background:#12355B;border-color:#12355B;color:#fff;font-weight:700;font-size:17.5px;letter-spacing:.4px;text-align:left;padding:11px 14px}
.pl-sheet .pl-head th{background:#EAF0F7;color:#12355B;font-weight:700;font-size:12.5px;letter-spacing:.8px;text-transform:uppercase;text-align:center;padding:7px 8px}
.pl-sheet .pl-head th.pl-y{background:#FFF1B8;color:#6B4E00}
/* terms + payment: bigger, same size; payment highlighted */
.pl-sheet table tr.pl-tc td{font-size:15px!important}
.pl-sheet table tr.pl-term td{font-size:15px!important;line-height:1.55!important}
.pl-sheet table tr.pl-pay td{font-size:15px!important}
.pl-sheet table tr.pl-tc td,.pl-sheet table tr.pl-term td{background:#FFE680!important;color:#4A3400!important;font-weight:700!important;font-size:15px!important}
.pl-sheet table tr.pl-pay td.pl-remark{background:#FFE680!important;color:#B91C1C!important;font-size:15px!important;font-weight:800!important;text-align:center!important;border:none!important}
.pl-sheet table tr.pl-pay td.pl-payment{font-weight:800!important;background:#FFE680!important;color:#B91C1C!important;letter-spacing:.3px}
.pl-sheet .pl-series{text-align:center;font-weight:700;color:#12355B;background:#F6F9FC}
.pl-sheet .pl-designs{font-size:14.5px;line-height:1.55;color:#36404d}
.pl-sheet .pl-price{text-align:center;font-weight:700;font-size:19px;color:#12355B;font-variant-numeric:tabular-nums}
.pl-sheet .pl-price:nth-child(3){background:#FFFBE6}
.pl-sheet .pl-foot td{background:#F1F5F9;color:#12355B;border:1px solid #cfd8e3}
.pl-sheet .pl-foot .pl-flabel{font-size:13.5px;font-weight:600}
.pl-sheet .pl-foot .pl-fmain{font-weight:700;font-size:14.5px;letter-spacing:.3px}
.pl-sheet .pl-tc td{background:transparent;color:#12355B;text-align:left;font-weight:700;font-size:13px;letter-spacing:.4px;border:none;border-bottom:2px solid #12355B;padding:6px 4px}
.pl-sheet .pl-term td{border:none;padding:3px 4px;font-size:12.5px;color:#36404d}
.pl-sheet .pl-pay td{border:none;padding:8px 4px}
.pl-sheet .pl-pay .pl-remark{font-weight:700;font-size:12px;color:#5b6776}
.pl-sheet .pl-pay .pl-payment{background:#12355B;color:#fff;font-weight:700;font-size:16px;letter-spacing:.6px;text-align:center;padding:13px 10px}
.pl-sheet .pl-gap td{border:none;padding:6px}
`;

const PL_CSS = `
.pl-bar{display:flex;gap:var(--space-2);flex-wrap:wrap;align-items:center;margin-bottom:var(--space-4)}
.pl-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:var(--space-3);margin-bottom:var(--space-3)}
.pl-lbl{display:flex;flex-direction:column;gap:4px;font-size:var(--font-size-xs);color:var(--color-text-muted);font-weight:600}
.pl-sec{border:1px solid var(--color-border);border-radius:var(--radius-md);padding:var(--space-4);margin-bottom:var(--space-4);background:var(--color-surface)}
.pl-sec-head{display:flex;gap:var(--space-2);align-items:flex-end;flex-wrap:wrap;margin-bottom:var(--space-3)}
.pl-grow{flex:1 1 260px}
.pl-group{display:grid;grid-template-columns:minmax(150px,1fr) minmax(220px,2.4fr) 84px 84px auto;gap:var(--space-2);align-items:start;padding:var(--space-3) 0;border-top:1px dashed var(--color-border)}
.pl-chips{display:flex;flex-wrap:wrap;gap:4px;padding:4px;border:1px solid var(--color-border-strong);border-radius:var(--radius-sm);background:var(--color-surface);min-height:36px;align-items:center}
.pl-chip{display:inline-flex;align-items:center;gap:4px;background:var(--color-accent-soft);color:var(--color-accent-hover);border-radius:10px;padding:2px 4px 2px 8px;font-size:11px;font-weight:600}
.pl-chip button{border:none;background:transparent;cursor:pointer;color:inherit;font-size:13px;line-height:1;padding:0 2px}
.pl-chips input{flex:1 1 120px;min-width:120px;border:none;outline:none;background:transparent;font-size:var(--font-size-xs);padding:4px;color:var(--color-text-primary)}
.pl-mini{display:flex;gap:2px}
.pl-mini button{border:1px solid var(--color-border);background:var(--color-surface);border-radius:4px;cursor:pointer;padding:2px 7px;font-size:12px;color:var(--color-text-secondary)}
.pl-mini button:disabled{opacity:.35;cursor:default}
.pl-hint{font-size:var(--font-size-xs);color:var(--color-text-muted)}
.pl-msg{padding:8px 12px;border-radius:var(--radius-sm);margin-bottom:var(--space-3);font-size:var(--font-size-sm)}
.pl-msg.err{background:#fbe9e7;color:#b3261e}
.pl-msg.ok{background:#e6f4ea;color:#1e6b3a}
.pl-list{width:100%;border-collapse:collapse}
.pl-list th{font-size:var(--font-size-xs);text-align:left;padding:8px 6px;color:var(--color-text-muted);border-bottom:1px solid var(--color-border)}
.pl-list td{padding:8px 6px;border-bottom:1px solid var(--color-border);font-size:var(--font-size-sm)}

/* The sheet itself: fixed light colours so print / PDF matches the sample */
${SHEET_CSS}
${SHEET_CSS.replace(/\.pl-sheet/g, '.pl-cap .pl-sheet')}
@media (max-width:760px){
  .pl-group{grid-template-columns:1fr 1fr}
  .pl-group > .pl-chipcell{grid-column:1 / -1}
  .pl-sheet td,.pl-sheet th{font-size:11px;padding:5px 6px}
  .pl-sheet .pl-top{font-size:17px}
  .pl-sheet .pl-title td{font-size:13px;padding:8px 10px}
  .pl-sheet .pl-price{font-size:14px}
  .pl-sheet .pl-payment{font-size:13px}
}

@media print{
  @page{size:A4 portrait;margin:8mm}
  body *{visibility:hidden}
  .pl-print-area,.pl-print-area *{visibility:visible}
  .pl-print-area{position:absolute;left:0;top:0;width:100%;margin:0;padding:0}
  .pl-noprint{display:none!important}
  .pl-sheet{max-width:none}
  .pl-sheet tr{page-break-inside:avoid}
  .pl-sheet *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
}
`;

/* ------------------------------------------------------------------ */
/* The printable sheet                                                 */
/* ------------------------------------------------------------------ */

export function PriceSheet({ doc }) {
  const show1 = plShow(doc, 'price1');
  const show2 = plShow(doc, 'price2');
  const nc = 2 + (show1 ? 1 : 0) + (show2 ? 1 : 0);
  const terms = termLines(doc.terms);
  const meta = [];
  const party = partyLine(doc);
  if (party) meta.push(`To: ${party}`);
  if (doc.list_date) meta.push(`Date: ${fmtDate(doc.list_date)}`);
  if (doc.valid_until) meta.push(`Valid until: ${fmtDate(doc.valid_until)}`);

  return (
    <div className="pl-sheet" data-testid="price-sheet">
      <table>
        <colgroup>
          <col style={{ width: nc === 4 ? '21%' : '20%' }} />
          <col style={{ width: nc === 4 ? '59%' : '64%' }} />
          {show1 && <col style={{ width: nc === 4 ? '10%' : '16%' }} />}
          {show2 && <col style={{ width: nc === 4 ? '10%' : '16%' }} />}
        </colgroup>
        <tbody>
          {(doc.title || meta.length > 0) && (
            <>
              {doc.title && <tr><td colSpan={nc} className="pl-top">{doc.title}</td></tr>}
              {meta.length > 0 && <tr><td colSpan={nc} className="pl-meta">{meta.join('   |   ')}</td></tr>}
            </>
          )}
          {doc.sections.map((s, si) => (
            <React.Fragment key={si}>
              <tr className="pl-title"><td colSpan={nc}>{s.heading}</td></tr>
              <tr className="pl-head">
                <th>SERIES</th>
                <th>DESIGN NAME</th>
                {plShow(doc, 'price1') && <th className="pl-y">{doc.col1_label}</th>}
                {plShow(doc, 'price2') && <th className="pl-y">{doc.col2_label}</th>}
              </tr>
              {s.groups.map((g, gi) => (
                <tr key={gi}>
                  <td className="pl-series">{g.series_name}</td>
                  <td className="pl-designs">{g.designs.join(' , ')}</td>
                  {plShow(doc, 'price1') && <td className="pl-price">{fmtPrice(g.price1)}</td>}
                  {plShow(doc, 'price2') && <td className="pl-price">{fmtPrice(g.price2)}</td>}
                </tr>
              ))}
              <tr className="pl-foot">
                <td className="pl-flabel">{footerLabel(s)}</td>
                <td colSpan={nc - 1} className="pl-fmain">
                  {s.packing ? `PACKING : ${s.packing}` : ''}
                  {s.packing && s.box_weight ? '     ' : ''}
                  {s.box_weight ? `BOX WEIGHT : ${s.box_weight}` : ''}
                </td>
              </tr>
            </React.Fragment>
          ))}
          {(terms.length > 0 || doc.payment_terms) && (
            <>
              <tr className="pl-gap"><td colSpan={nc} /></tr>
              {terms.length > 0 && <tr className="pl-tc"><td colSpan={nc}>Terms &amp; Conditions:</td></tr>}
              {terms.map((t, i) => (
                <tr key={i} className="pl-term"><td colSpan={nc}>{t}</td></tr>
              ))}
              {doc.payment_terms && (
                <tr className="pl-pay">
                  <td className="pl-remark">{doc.special_remark}</td>
                  <td colSpan={nc - 1} className="pl-payment">PAYMENT TERMS : {doc.payment_terms}</td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Editor pieces                                                       */
/* ------------------------------------------------------------------ */

function DesignChips({ value, onChange, listId }) {
  const [draft, setDraft] = useState('');

  const commit = (raw) => {
    const parts = String(raw).split(/[,\n]/).map((p) => p.trim().toUpperCase()).filter(Boolean);
    if (parts.length === 0) return;
    const next = [...value];
    parts.forEach((p) => { if (!next.includes(p)) next.push(p); });
    onChange(next);
    setDraft('');
  };

  return (
    <div className="pl-chips">
      {value.map((d, i) => (
        <span className="pl-chip" key={`${d}-${i}`}>
          {d}
          <button type="button" aria-label={`Remove ${d}`} onClick={() => onChange(value.filter((_, j) => j !== i))}>&times;</button>
        </span>
      ))}
      <input
        list={listId}
        value={draft}
        placeholder={value.length ? 'add design…' : 'Type a design and press Enter (or paste a comma-separated list)'}
        onChange={(e) => {
          const v = e.target.value;
          if (v.includes(',') || v.includes('\n')) commit(v); else setDraft(v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(draft); }
          if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={() => commit(draft)}
        onPaste={(e) => {
          const t = e.clipboardData && e.clipboardData.getData('text');
          if (t && /[,\n]/.test(t)) { e.preventDefault(); commit(t); }
        }}
      />
    </div>
  );
}

function MoveButtons({ index, count, onMove, onRemove }) {
  return (
    <div className="pl-mini">
      <button type="button" title="Move up" disabled={index === 0} onClick={() => onMove(-1)}>&uarr;</button>
      <button type="button" title="Move down" disabled={index === count - 1} onClick={() => onMove(1)}>&darr;</button>
      <button type="button" title="Remove" onClick={onRemove}>&times;</button>
    </div>
  );
}

function moveItem(arr, i, dir) {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

function SectionEditor({ section, index, count, doc, onChange, onMove, onRemove, listId, sizeOptions }) {
  const set = (patch) => onChange({ ...section, ...patch });
  const setGroup = (gi, patch) => set({ groups: section.groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) });
  const manual = useRef(new Set());
  const lastAnchor = useRef(-1);
  const kind = sizeKind(section.heading);
  const roleAt = (gi) => roleOf(kind, section.groups[gi] && section.groups[gi].series_name);
  const setPrice = (gi, field, value) => {
    const role = roleAt(gi);
    if (field === 'price1' && isAnchor(kind, role)) {
      const next = applyAnchor(section, gi, value, manual.current);
      if (next) {
        lastAnchor.current = gi;
        set({ groups: next });
        return;
      }
      setGroup(gi, { price1: value });
      return;
    }
    if (role) manual.current.add(`${role}:${field === 'price1' ? 'p1' : 'p2'}`);
    setGroup(gi, { [field]: value });
  };
  const autoAll = () => {
    manual.current = new Set();
    let gi = lastAnchor.current;
    if (gi < 0 || !isAnchor(kind, roleAt(gi))) gi = section.groups.findIndex((g, i) => isAnchor(kind, roleAt(i)) && g.price1 !== '' && g.price1 !== null);
    if (gi < 0) return;
    const next = applyAnchor(section, gi, section.groups[gi].price1, manual.current);
    if (next) set({ groups: next });
  };
  const small = { ...inputStyle, width: '100%' };

  return (
    <div className="pl-sec">
      <div className="pl-sec-head">
        <label className="pl-lbl pl-grow">
          Section heading (size)
          <input
            style={small}
            value={section.heading}
            list="pl-sizes"
            placeholder="e.g. 600x600 mm DOUBLE CHARGED (2x2)"
            onChange={(e) => set({ heading: e.target.value })}
          />
        </label>
        <label className="pl-lbl" style={{ width: 150 }}>
          Packing
          <input style={small} value={section.packing} placeholder="4 pc/Box" onChange={(e) => set({ packing: e.target.value })} />
        </label>
        <label className="pl-lbl" style={{ width: 190 }}>
          Box weight
          <input style={small} value={section.box_weight} placeholder="23.5-24 kg Approx" onChange={(e) => set({ box_weight: e.target.value })} />
        </label>
        <label className="pl-lbl" style={{ width: 200 }}>
          Footer label (optional)
          <input style={small} value={section.footer_label} placeholder={footerLabel(section) || 'auto'} onChange={(e) => set({ footer_label: e.target.value })} />
        </label>
        <MoveButtons index={index} count={count} onMove={(d) => onMove(d)} onRemove={onRemove} />
      </div>
      <datalist id="pl-sizes">
        {sizeOptions.map((s) => <option key={s} value={s} />)}
      </datalist>

      {section.groups.map((g, gi) => (
        <div className="pl-group" key={gi}>
          <label className="pl-lbl">
            Series
            <input style={small} value={g.series_name} placeholder="e.g. PRO Series" onChange={(e) => setGroup(gi, { series_name: e.target.value })} />
          </label>
          <div className="pl-lbl pl-chipcell">
            Designs ({g.designs.length})
            <DesignChips value={g.designs} listId={listId} onChange={(designs) => setGroup(gi, { designs })} />
          </div>
          <label className="pl-lbl">
            {doc.col1_label}
            <input style={small} type="number" min="0" step="0.5" value={g.price1} onChange={(e) => setPrice(gi, 'price1', e.target.value)} />
          </label>
          <label className="pl-lbl">
            {doc.col2_label}
            <input style={small} type="number" min="0" step="0.5" value={g.price2} onChange={(e) => setPrice(gi, 'price2', e.target.value)} />
          </label>
          <MoveButtons
            index={gi}
            count={section.groups.length}
            onMove={(d) => set({ groups: moveItem(section.groups, gi, d) })}
            onRemove={() => set({ groups: section.groups.filter((_, i) => i !== gi) })}
          />
        </div>
      ))}
      <Button variant="secondary" onClick={() => set({ groups: [...section.groups, EMPTY_GROUP()] })}>+ Add series group</Button>
      {kind && (
        <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--color-text-muted)' }}>
          Auto rates ({kind}): {kind === '600x600' ? 'PRO ke MELODY Light' : 'MELODY Light'} nu PREM rate nakho &rarr; STD ane bija badha series na rate automatic aavi jashe. Koi pan rate tame manually badli shako.{' '}
          <button type="button" onClick={autoAll} style={{ border: 'none', background: 'none', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>Re-apply auto rates</button>
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function PriceListPage() {
  const { user } = useAuth();
  const perms = user && Array.isArray(user.permissions) ? new Set(user.permissions) : null;
  const can = () => !perms || perms.has('price_list.view'); // one access = whole module

  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null); // the list being edited / viewed
  const [docId, setDocId] = useState(null);
  const [options, setOptions] = useState({ designs: [], sizes: [] });
  const [busy, setBusy] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [showCustomers, setShowCustomers] = useState(false);
  const [custQ, setCustQ] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const pdfCache = useRef({ key: '', file: null });
  const imgCache = useRef({ key: '', file: null });
  const scanInput = useRef(null);
  const galleryInput = useRef(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanText, setScanText] = useState('');
  const [cardLines, setCardLines] = useState({ candidates: [], auto: '' });
  useEffect(() => {
    if (!doc) return undefined;
    let dead = false;
    const t = setTimeout(async () => {
      try {
        const f = await buildSheetPdfFile(doc.title);
        if (!dead && f) pdfCache.current = { key: JSON.stringify(doc), file: f };
        const im = await buildSheetImageFile(doc.title);
        if (!dead && im) imgCache.current = { key: JSON.stringify(doc), file: im };
      } catch (e) { /* ignore - built again on click */ }
    }, 1200);
    return () => { dead = true; clearTimeout(t); };
  }, [doc]);

  const loadLists = () => api.listPriceLists()
    .then(setLists)
    .catch((e) => setError(errorMessage(e)))
    .finally(() => setLoading(false));

  useEffect(() => {
    loadLists();
    api.getPriceListOptions().then(setOptions).catch(() => {});
  }, []);

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(''), 3000);
  };

  const startNew = (sample) => {
    setError('');
    setDocId(null);
    setDoc(sample ? SAMPLE_DOC() : NEW_DOC());
  };

  const openList = async (id) => {
    setError('');
    setBusy(true);
    try {
      const d = await api.getPriceList(id);
      setDocId(id);
      setDoc({
        ...NEW_DOC(),
        ...d,
        customer_name: d.customer_name || '',
        customer_mobile: d.customer_mobile || '',
        customer_address: d.customer_address || '',
        customer_city: d.customer_city || '',
        customer_state: d.customer_state || '',
        list_date: d.list_date || '',
        valid_until: d.valid_until || '',
        terms: d.terms || '',
        special_remark: d.special_remark || '',
        payment_terms: d.payment_terms || '',
        sections: d.sections.map((s) => ({
          ...s,
          groups: s.groups.map((g) => ({ ...g, price1: g.price1 === null ? '' : g.price1, price2: g.price2 === null ? '' : g.price2 })),
        })),
      });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setError('');
    setBusy(true);
    try {
      const saved = docId ? await api.updatePriceList(docId, doc) : await api.createPriceList(doc);
      setDocId(saved.price_list_id);
      flash('Saved');
      loadLists();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async (id) => {
    setBusy(true);
    try {
      await api.duplicatePriceList(id);
      flash('Duplicated');
      await loadLists();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete price list "${item.title}"?`)) return;
    setBusy(true);
    try {
      await api.deletePriceList(item.price_list_id);
      flash('Deleted');
      await loadLists();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const copyText = async () => {
    const ok = await copyToClipboard(buildShareText(doc));
    flash(ok ? 'Text copied - paste it in WhatsApp' : 'Copy failed - please copy manually');
  };

  const designListId = 'pl-designs';
  const designDatalist = useMemo(() => (
    <datalist id={designListId}>
      {options.designs.map((d) => <option key={d} value={d} />)}
    </datalist>
  ), [options.designs]);

  const setDocField = (patch) => setDoc((d) => ({ ...d, ...patch }));
  const setSection = (si, next) => setDoc((d) => ({ ...d, sections: d.sections.map((s, i) => (i === si ? next : s)) }));

  const scanCard = async (file) => {
    if (!file) return;
    setError('');
    setScanBusy(true);
    setScanProgress(0);
    try {
      const out = await readCardText(file, setScanProgress);
      const text = typeof out === 'string' ? out : out.text;
      const found = parseCard(typeof out === 'string' ? out : out.lines, { ignore: loadIgnore() });
      setScanText(text);
      setCardLines({ candidates: found.candidates || [], auto: found.company || '' });
      const patch = {};
      ['customer_name', 'customer_mobile', 'customer_address', 'customer_city', 'customer_state'].forEach((k) => {
        if (found[k]) patch[k] = found[k];
      });
      if (Object.keys(patch).length === 0) {
        setError('Could not read the card clearly. Take the photo again in good light, or type the details.');
      } else {
        setDocField(patch);
        flash('Card read - please check the party details');
      }
    } catch (e) {
      setError('Could not read the card: ' + (e && e.message ? e.message : e));
    } finally {
      setScanBusy(false);
    }
  };

  const pickPartyName = (line) => {
    setDocField({ customer_name: line });
    const old = cardLines.auto;
    if (old && old !== line) {
      const list = loadIgnore();
      if (!list.includes(old)) saveIgnore([...list, old].slice(-30));
      flash(`Party name set. "${old}" will be ignored in future scans.`);
    }
  };

  const clearIgnored = () => {
    saveIgnore([]);
    flash('Ignored names cleared');
  };

  const sendWhatsApp = async () => {
    setError('');
    const num = whatsappNumber(doc.customer_mobile);
    if (!num) {
      setError('Enter the party mobile number first (or scan the visiting card).');
      return;
    }
    setBusy(true);
    try {
      /* 1) copy the price-list image to the clipboard (must start inside the click) */
      const canClip = typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.write === 'function' && typeof window.ClipboardItem === 'function';
      let clipDone = Promise.resolve(false);
      if (canClip) {
        const pngPromise = (async () => {
          const key = JSON.stringify(doc);
          let img = imgCache.current.key === key ? imgCache.current.file : null;
          if (!img) {
            img = await buildSheetImageFile(doc.title);
            if (img) imgCache.current = { key, file: img };
          }
          return imageFileToPngBlob(img);
        })();
        pngPromise.catch(() => null);
        clipDone = navigator.clipboard.write([new window.ClipboardItem({ 'image/png': pngPromise })]).then(() => true, () => false);
      }
      const [saved, copied] = await Promise.all([
        docId ? api.updatePriceList(docId, doc) : api.createPriceList(doc),
        clipDone,
      ]);
      setDocId(saved.price_list_id);
      const lines = [];
      lines.push(doc.customer_name ? `Namaste ${doc.customer_name},` : 'Namaste,');
      lines.push('');
      lines.push(`${doc.title || 'Price List'}${doc.list_date ? ` (${fmtDate(doc.list_date)})` : ''}`);
      lines.push(copied ? 'Please find our price list below.' : 'Please find our price list (PDF) attached.');  // DIRECT_CHAT2
      if (doc.valid_until) lines.push(`Valid until: ${fmtDate(doc.valid_until)}`);
      if (doc.payment_terms) lines.push(`Payment terms: ${doc.payment_terms}`);
      const text = lines.join('\n');
      loadLists();
      if (!copied) {
        /* clipboard not available: download the PDF so it can be attached by hand */
        const file = await makePdf().catch(() => null);
        if (file) {
          const url = URL.createObjectURL(file);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        }
      }
      /* the party's own chat opens; the image is on the clipboard - long-press in the message box > Paste > Send */
      window.location.href = `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /* ---------------------------- list view ---------------------------- */
  if (!doc) {
    return (
      <div>
        <style>{PL_CSS}</style>
        <h1 style={{ marginTop: 0 }}>Price List</h1>
        {error && <div className="pl-msg err" role="alert">{error}</div>}
        {notice && <div className="pl-msg ok">{notice}</div>}
        <div className="pl-bar">
          {can('create') && <Button onClick={() => startNew(false)}>+ New price list</Button>}
          {can('create') && <Button variant="secondary" onClick={() => setCustomizing(true)}>Customize Price List</Button>}
          {can('create') && <Button variant="secondary" onClick={() => startNew(true)}>New from example (600x600 &amp; 1200x600)</Button>}
          <Button variant="secondary" onClick={() => setShowCustomers((v) => !v)}>Customer Price List</Button>
        </div>
        {customizing && (
        <CustomizePriceList
          catalog={options.catalog}
          sample={SAMPLE_DOC().sections}
          history={lists.filter((x) => x.is_customer)}
          fetchList={(id) => api.getPriceList(id)}
          onClose={() => setCustomizing(false)}
          onCreate={(patch) => {
            setError('');
            setDocId(null);
            setCustomizing(false);
            setDoc({ ...NEW_DOC(), title: 'PRICE LIST', terms: DEFAULT_TERMS, payment_terms: 'ADVANCE PAYMENT', ...patch });
          }}
        />
      )}
      {showCustomers && (
        <Card title="Customer price lists">
          <input
            style={{ ...inputStyle, width: '100%', maxWidth: 380, marginBottom: 12 }}
            value={custQ}
            placeholder="Search customer, city, mobile, date..."
            onChange={(e) => setCustQ(e.target.value)}
          />
          {(() => {
            const needle = custQ.trim().toLowerCase();
            const rowsC = lists
              .filter((x) => x.is_customer)
              .filter((x) => !needle || [x.customer_name, x.customer_city, x.customer_mobile, x.title, fmtDate(x.list_date), x.updated_at].join(' ').toLowerCase().includes(needle));
            if (loading) return <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>;
            if (!rowsC.length) {
              return <p style={{ color: 'var(--color-text-muted)' }}>{needle ? 'Kai malyu nahi.' : 'Haju koi customer price list nathi. "Customize Price List" thi banavo ane Save karo.'}</p>;
            }
            return (
              <div className="table-scroll">
                <table className="pl-list">
                  <thead>
                    <tr><th>Customer</th><th>City</th><th>Date</th><th>Sizes</th><th>Updated</th><th /></tr>
                  </thead>
                  <tbody>
                    {rowsC.map((l) => (
                      <tr key={l.price_list_id}>
                        <td><strong>{l.customer_name || '—'}</strong>{l.customer_mobile ? <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{l.customer_mobile}</div> : null}</td>
                        <td>{l.customer_city || '—'}</td>
                        <td>{fmtDate(l.list_date) || '—'}</td>
                        <td>{l.section_count}</td>
                        <td>{l.updated_at}</td>
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                          <Button variant="text" onClick={() => openList(l.price_list_id)} disabled={busy}>Open</Button>
                          {can('create') && <Button variant="text" onClick={() => duplicate(l.price_list_id)} disabled={busy}>Duplicate</Button>}
                          {can('delete') && <Button variant="text" onClick={() => remove(l)} disabled={busy}>Delete</Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </Card>
      )}
      <Card title="Saved price lists">
          {loading ? (
            <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
          ) : lists.filter((x) => !x.is_customer).length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)' }}>No price list yet. Use &ldquo;New from example&rdquo; to start from the standard layout.</p>
          ) : (
            <div className="table-scroll">
              <table className="pl-list">
                <thead>
                  <tr><th>Title</th><th>Customer</th><th>Date</th><th>Valid until</th><th>Sizes</th><th>Updated</th><th /></tr>
                </thead>
                <tbody>
                  {lists.filter((x) => !x.is_customer).map((l) => (
                    <tr key={l.price_list_id}>
                      <td><strong>{l.title}</strong></td>
                      <td>{l.customer_name || '—'}{l.customer_mobile ? <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{l.customer_mobile}</div> : null}</td>
                      <td>{fmtDate(l.list_date) || '—'}</td>
                      <td>{fmtDate(l.valid_until) || '—'}</td>
                      <td>{l.section_count}</td>
                      <td>{l.updated_at}</td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <Button variant="text" onClick={() => openList(l.price_list_id)} disabled={busy}>Open</Button>
                        {can('create') && <Button variant="text" onClick={() => duplicate(l.price_list_id)} disabled={busy}>Duplicate</Button>}
                        {can('delete') && <Button variant="text" onClick={() => remove(l)} disabled={busy}>Delete</Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    );
  }

  /* --------------------------- editor view --------------------------- */
  const canSave = docId ? can('edit') : can('create');
  const makePdf = async () => {
    const key = JSON.stringify(doc);
    if (pdfCache.current.key === key && pdfCache.current.file) return pdfCache.current.file;
    const f = await buildSheetPdfFile(doc.title);
    if (f) pdfCache.current = { key, file: f };
    return f;
  };
  const downloadPdf = async () => {
    setError('');
    setPdfBusy(true);
    try {
      const file = await makePdf();
      if (!file) return;
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      flash('PDF downloaded: ' + file.name);
    } catch (e) {
      setError('Could not create PDF: ' + (e && e.message ? e.message : e));
    } finally {
      setPdfBusy(false);
    }
  };
  const shareImage = async () => {
    setError('');
    if (!canShareFiles) {
      setError('This browser cannot share files directly. Use Download PDF and attach it in WhatsApp.');
      return;
    }
    const cached = imgCache.current.key === JSON.stringify(doc) ? imgCache.current.file : null;
    if (!cached) {
      setPdfBusy(true);
      try {
        const f = await buildSheetImageFile(doc.title);
        if (f) imgCache.current = { key: JSON.stringify(doc), file: f };
        flash('Image is ready - tap Share Image once more');
      } catch (e) {
        setError('Could not create image: ' + (e && e.message ? e.message : e));
      } finally {
        setPdfBusy(false);
      }
      return;
    }
    try {
      await navigator.share({ files: [cached] });
    } catch (e) {
      if (!e || e.name !== 'AbortError') setError('Could not share image: ' + (e && e.message ? e.message : e));
    }
  };
  const canShareFiles = typeof navigator !== 'undefined' && typeof navigator.canShare === 'function' &&
    typeof File === 'function' && (() => { try { return navigator.canShare({ files: [new File(['a'], 'a.pdf', { type: 'application/pdf' })] }); } catch (e) { return false; } })();
  const printSheet = () => {
    const node = document.querySelector('.pl-print-area');
    if (!node) { window.print(); return; }
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) { setError('Popup blocked - allow popups for this site, then click the button again.'); return; }
    const sheetCss = PL_CSS.split('@media (max-width:760px)')[0];
    const ttl = String(doc.title || 'Price List').replace(/[<>&]/g, ' ');
    w.document.open();
    w.document.write('<!doctype html><html><head><meta charset="utf-8"><title>' + ttl + '</title><style>' + sheetCss +
      '@page{size:A4 portrait;margin:8mm}body{margin:0;background:#fff}.pl-sheet{max-width:none}.pl-sheet tr{page-break-inside:avoid}' +
      '.pl-sheet *{-webkit-print-color-adjust:exact;print-color-adjust:exact}</style></head><body>' + node.innerHTML + '</body></html>');
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* ignore */ } }, 500);
  };
  const small = { ...inputStyle, width: '100%' };
  return (
    <div>
      <style>{PL_CSS}</style>
      {designDatalist}
      <div className="pl-noprint">
        <h1 style={{ marginTop: 0 }}>{docId ? 'Edit price list' : 'New price list'}</h1>
        {error && <div className="pl-msg err" role="alert">{error}</div>}
        {notice && <div className="pl-msg ok">{notice}</div>}
        {scanText && (
          <details className="pl-msg ok" style={{ marginBottom: 'var(--space-3)' }}>
            <summary>Card text that was read (tap to check)</summary>
            <pre style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0', fontSize: 12 }}>{scanText}</pre>
          </details>
        )}
        {cardLines.candidates.length > 1 && (
          <div className="pl-msg ok" style={{ marginBottom: 'var(--space-3)' }}>
            <div style={{ marginBottom: 6 }}>Wrong party name (brand / logo)? Tap the correct line from the card:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {cardLines.candidates.map((l) => (
                <button
                  type="button"
                  key={l}
                  onClick={() => pickPartyName(l)}
                  style={{
                    padding: '6px 10px', borderRadius: 16, border: '1px solid var(--color-border)', cursor: 'pointer',
                    background: doc.customer_name.startsWith(l) ? 'var(--color-accent)' : '#fff',
                    color: doc.customer_name.startsWith(l) ? '#fff' : '#000',
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
            {loadIgnore().length > 0 && (
              <div style={{ marginTop: 6, fontSize: 12 }}>
                Ignored names: {loadIgnore().join(', ')}{' '}
                <button type="button" onClick={clearIgnored} style={{ border: 'none', background: 'none', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>Clear</button>
              </div>
            )}
          </div>
        )}
        <div className="pl-bar">
          <Button variant="secondary" onClick={() => { setDoc(null); setDocId(null); setError(''); }}>&larr; All price lists</Button>
          {canSave && <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>}
          {canSave && (
            <Button variant="secondary" onClick={() => scanInput.current && scanInput.current.click()} disabled={scanBusy}>
              {scanBusy ? `Reading card… ${scanProgress}%` : 'Scan card (camera)'}
            </Button>
          )}
          {canSave && (
            <Button variant="secondary" onClick={() => galleryInput.current && galleryInput.current.click()} disabled={scanBusy}>
              Upload card photo
            </Button>
          )}
          {canSave && can('export') && <Button onClick={sendWhatsApp} disabled={busy}>Send on WhatsApp</Button>}
          <input
            ref={scanInput}
            type="file"
            accept="image/*"
            capture="environment"
            data-testid="card-input"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; scanCard(f); }}
          />
          <input
            ref={galleryInput}
            type="file"
            accept="image/*"
            data-testid="card-gallery-input"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; scanCard(f); }}
          />
          {can('export') && <Button variant="secondary" onClick={downloadPdf} disabled={pdfBusy}>{pdfBusy ? 'Making PDF…' : 'Download PDF'}</Button>}
          {can('export') && <Button variant="secondary" onClick={shareImage} disabled={pdfBusy}>Share Image</Button>}
          {can('export') && <Button variant="secondary" onClick={printSheet}>Print</Button>}
          {can('export') && <Button variant="secondary" onClick={() => window.open('https://wa.me/?text=' + encodeURIComponent(buildShareText(doc)), '_blank')}>WhatsApp text</Button>}
          {can('export') && <Button variant="secondary" onClick={copyText}>Copy WhatsApp text</Button>}
        </div>

        <Card title="Details">
          <div className="pl-grid">
            <label className="pl-lbl">Title *
              <input style={small} value={doc.title} placeholder="PRICE LIST - October 2026" onChange={(e) => setDocField({ title: e.target.value })} />
            </label>
            <label className="pl-lbl">Party name
              <input style={small} value={doc.customer_name} onChange={(e) => setDocField({ customer_name: e.target.value })} />
            </label>
            <label className="pl-lbl">Party mobile (WhatsApp)
              <input style={small} type="tel" inputMode="tel" value={doc.customer_mobile} placeholder="98765 43210" onChange={(e) => setDocField({ customer_mobile: e.target.value })} />
            </label>
            <label className="pl-lbl">Address
              <input style={small} value={doc.customer_address} onChange={(e) => setDocField({ customer_address: e.target.value })} />
            </label>
            <label className="pl-lbl">City
              <input style={small} value={doc.customer_city} onChange={(e) => setDocField({ customer_city: e.target.value })} />
            </label>
            <label className="pl-lbl">State
              <input style={small} value={doc.customer_state} onChange={(e) => setDocField({ customer_state: e.target.value })} />
            </label>
            <label className="pl-lbl">Date
              <DateInput style={small} value={doc.list_date} onChange={(e) => setDocField({ list_date: e.target.value })} />
            </label>
            <label className="pl-lbl">Valid until
              <DateInput style={small} value={doc.valid_until} onChange={(e) => setDocField({ valid_until: e.target.value })} />
            </label>
            <label className="pl-lbl">Price column 1
              <input style={small} value={doc.col1_label} onChange={(e) => setDocField({ col1_label: e.target.value })} />
            </label>
            <label className="pl-lbl">Price column 2
              <input style={small} value={doc.col2_label} onChange={(e) => setDocField({ col2_label: e.target.value })} />
            </label>
          </div>
        </Card>

        <div style={{ height: 'var(--space-4)' }} />
        {doc.sections.map((s, si) => (
          <SectionEditor
            key={si}
            section={s}
            index={si}
            count={doc.sections.length}
            doc={doc}
            listId={designListId}
            sizeOptions={options.sizes}
            onChange={(next) => setSection(si, next)}
            onMove={(d) => setDocField({ sections: moveItem(doc.sections, si, d) })}
            onRemove={() => setDocField({ sections: doc.sections.filter((_, i) => i !== si) })}
          />
        ))}
        <div className="pl-bar">
          <Button variant="secondary" onClick={() => setDocField({ sections: [...doc.sections, EMPTY_SECTION()] })}>+ Add size section</Button>
        </div>

        <Card title="Terms &amp; payment">
          <label className="pl-lbl" style={{ marginBottom: 'var(--space-3)' }}>Terms &amp; Conditions (one per line)
            <textarea style={{ ...small, minHeight: 80, fontFamily: 'inherit' }} value={doc.terms} onChange={(e) => setDocField({ terms: e.target.value })} />
          </label>
          <div className="pl-grid">
            <label className="pl-lbl">Left label
              <input style={small} value={doc.special_remark} onChange={(e) => setDocField({ special_remark: e.target.value })} />
            </label>
            <label className="pl-lbl">Payment terms
              <input style={small} value={doc.payment_terms} placeholder="ADVANCE PAYMENT" onChange={(e) => setDocField({ payment_terms: e.target.value })} />
            </label>
          </div>
          <p className="pl-hint" style={{ marginBottom: 0 }}>
            Tip: Print / Save as PDF opens your browser&rsquo;s print window - choose &ldquo;Save as PDF&rdquo; to share the file.
          </p>
        </Card>

        <h2 style={{ marginTop: 'var(--space-6)' }}>Preview</h2>
      </div>
      <div className="pl-print-area">
        <PriceSheet doc={doc} />
      </div>
    </div>
  );
}
